import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  Drawer,
  IconButton,
  LinearProgress,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material'
import { DataGrid, GridColumnMenu } from '@mui/x-data-grid'
import moment from 'moment'
import React, { useContext, useEffect, useState } from 'react'
import SentimentVeryDissatisfiedIcon from '@mui/icons-material/SentimentVeryDissatisfied'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import { LoadingSmall } from '../../../loading/loadingPage'
import PriceCheckIcon from '@mui/icons-material/PriceCheck'
import API from '../../../../services/API'
import CustomDataGrid from '../../../../components/DataGrid/CustomDataGrid'
import { formatMoney } from '../../../../helpers/currency'
import { TenantSettingsContext } from '../../../../App'
import { PayrollContext } from './Payroll'
import { KeyboardArrowRight } from '@mui/icons-material'
import UnsavedChanges from '../../../../components/UnsavedChanges/UnsavedChanges'
import { useNavigate } from 'react-router-dom'

const getShiftWorkerName = (shift) => {
  const userAccount =
    shift?.assignedTo?.userAccount ?? shift?.assignedSubcontractor?.userAccount
  const name = [userAccount?.firstName, userAccount?.lastName]
    .filter(Boolean)
    .join(' ')
    .trim()

  if (name) return name
  if (shift?.assignedSubcontractor || shift?.assignedToSubcontractor) {
    return 'Contractor'
  }
  return 'Unassigned'
}

const SearchResults = () => {
  const { orgSettings } = useContext(TenantSettingsContext).orgConfig
  const {
    results,
    fetchError,
    setSelectedShift,
    loading,
    loadShifts,
    handleCreatePayRun,
    highlightedShifts,
    setHighlightedShifts,
    selectedExpenses,
    setSelectedExpenses,
  } = useContext(PayrollContext)
  const navigate = useNavigate()

  const [columnVisibilityModel, setColumnVisibilityModel] = useState({
    pinNumber: false,
    employee: true,
    shiftStart: true,
    shiftEnd: true,
    shiftName: true,
    organisation: false,
    overtime: true,
    overtimeApprovedBy: false,
    overtimeReason: false,
    payDue: true,
    payPer: false,
    payRun: true,
    isPaid: true,
    hasInvoice: true,
  })

  const visibleFields = Object.keys(columnVisibilityModel).filter(
    (key) => columnVisibilityModel[key] === true,
  )

  return (
    <>
      <Stack gap={2}>
        {loading && <LinearProgress color="success" />}
        <CustomDataGrid
          columns={[
            {
              field: 'pinNumber',
              headerName: 'Employee PIN',
              valueGetter: (value, row) => row?.assignedTo?.pinNumber ?? '',
              minWidth: 150,
              renderForExport: (row) => row?.assignedTo?.pinNumber ?? '',
            },
            {
              field: 'employee',
              headerName: 'Employee',
              valueGetter: (value, row) => getShiftWorkerName(row),
              flex: 1,
              minWidth: 250,
              renderForExport: (row) => getShiftWorkerName(row),
            },
            {
              field: 'shiftStart',
              headerName: 'Shift Start',
              valueGetter: (value, row) =>
                moment(row.start).format('DD MMM YYYY HH:mm').toString(),
              renderCell: ({ row }) => (
                <Stack>
                  <Typography variant="body2">
                    {moment(row.start).format('HH:mm')}
                  </Typography>
                  <Typography variant="caption">
                    {moment(row.start).format('Do MMM YYYY')}
                  </Typography>
                </Stack>
              ),
              flex: 0.4,
              minWidth: 120,
              renderForExport: (row) =>
                `${moment(row.start).format('ddd Do MMM')}\n${moment(
                  row.start,
                ).format('HH:mm')}hrs`,
            },
            {
              field: 'shiftEnd',
              headerName: 'Shift End',
              valueGetter: (value, row) =>
                moment(row.end).format('DD MMM YYYY HH:mm').toString(),
              renderCell: ({ row }) => (
                <Stack>
                  <Typography variant="body2">
                    {moment(row.end).format('HH:mm')}
                  </Typography>
                  <Typography variant="caption">
                    {moment(row.end).format('Do MMM YYYY')}
                  </Typography>
                </Stack>
              ),
              renderForExport: (row) =>
                `${moment(row.end).format('ddd Do MMM')}\n${moment(
                  row.end,
                ).format('HH:mm')}hrs`,
              flex: 0.4,
              minWidth: 120,
            },
            {
              field: 'shiftName',
              headerName: 'Shift',
              valueGetter: (value, row) =>
                row?.event?.title
                  ? `Event - ${row.event.title}`
                  : row.ptsDuty
                    ? `PTS${row.ptsDuty?.dutyRef ? ` - ${row.ptsDuty.dutyRef}` : ''}`
                    : row.roster
                      ? 'Rostered'
                      : '',
              flex: 0.3,
              minWidth: 100,
              renderCell: ({ row }) => (
                <Stack>
                  <Typography variant="body2">{row?.shiftType}</Typography>
                  <Typography variant="caption">
                    {row?.event
                      ? row?.event?.title
                      : row?.ptsDuty
                        ? (row?.ptsDuty?.dutyRef ?? '\u00A0')
                        : row?.frontlineDuty
                          ? '\u00A0'
                          : '\u00A0'}
                  </Typography>
                </Stack>
              ),
              renderForExport: (row) =>
                row?.event?.title
                  ? row.event.title
                  : row.ptsDuty
                    ? 'PTS'
                    : row.roster
                      ? 'Rostered'
                      : '',
            },
            {
              field: 'organisation',
              headerName: 'Organisation',
              valueGetter: (value, row) =>
                row?.event?.organisation
                  ? row?.event?.organisation?.orgName
                  : 'Not an Event',
              minWidth: 200,
              flex: 0.8,
              renderForExport: (row) =>
                row?.event?.organisation
                  ? row?.event?.organisation?.orgName
                  : 'Not an Event',
            },
            {
              field: 'overtime',
              headerName: 'Overtime',
              exportHeaderName: 'Overtime\nApproved',
              valueGetter: (value, row) =>
                row?.overTime?.decidedMinutes ??
                row?.overTime?.roundedMinutes ??
                0,
              renderForExport: (row) =>
                !row?.overTime?.required
                  ? ('')
                  : row?.overTime?.status === 'Pending'
                    ? ('Pending')
                    : (row?.overTime?.decidedMinutes ??
                      row?.overTime?.roundedMinutes ??
                      `${row?.overTime?.roundedMinutes ?? 0} mins`),
              renderCell: ({ row }) =>
                !row?.overTime?.required ? (
                  '-'
                ) : row?.overTime?.status === 'Pending' ? (
                  <Button
                    variant="contained"
                    onClick={() =>
                      navigate(`/overtime-claims?shift=${row._id}`)
                    }
                    color="contrast"
                    size="small"
                  >
                    Pending
                  </Button>
                ) : (
                  <Stack>
                    <Typography variant="body2">
                      {row?.overTime?.decidedMinutes ??
                        row?.overTime?.roundedMinutes ??
                        0}{' '}
                      mins
                    </Typography>
                    {row?.overTime?.status ? (
                      <Typography
                        variant="caption"
                        color={row?.overTime?.status === 'Pending'}
                      >
                        {row?.overTime?.status ? row?.overTime?.status : '-'}
                      </Typography>
                    ) : (
                      ''
                    )}
                  </Stack>
                ),
              minWidth: 150,
              align: 'center',
              headerAlign: 'center',
            },
            {
              field: 'overtimeApprovedBy',
              headerName: 'Overtime Approved By',
              minWidth: 200,
              valueGetter: (value, row) =>
                `${row?.overTime?.decidedBy?.firstName ?? ''} ${row?.overTime?.decidedBy?.lastName ?? ''}`,
              renderForExport: (row) =>
                `${row?.overTime?.decidedBy?.firstName ?? ''} ${row?.overTime?.decidedBy?.lastName ?? ''}`,
            },
            {
              field: 'overtimeReason',
              headerName: 'Overtime Reason',
              minWidth: 200,
              valueGetter: (value, row) =>
                row?.overTime?.otherReason ??
                row?.overTime?.correctedReason ??
                row?.overTime?.reason ??
                '',
              renderForExport: (row) =>
                row?.overTime?.otherReason ??
                row?.overTime?.correctedReason ??
                row?.overTime?.reason ??
                '',
            },
            {
              field: 'payDue',
              headerName: 'Pay Due',
              // The server virtual serialises the total as a string, which
              // sorts alphabetically - coerce it so the column sorts by value
              type: 'number',
              valueGetter: (value, row) => Number(row?.payDue?.total ?? 0),
              valueFormatter: (value) =>
                formatMoney(value, orgSettings?.currency),
              renderForExport: (row) => row?.payDue?.total ?? 0,
              minWidth: 120,
            },
            {
              field: 'payPer',
              headerName: 'Pay Per',
              valueGetter: (value, row) => (row.hourlyRate ? 'Hour' : 'Shift'),
              renderForExport: (row) => (row.hourlyRate ? 'Hour' : 'Shift'),
            },
            {
              field: 'payRun',
              headerName: 'Pay Run',
              minWidth: 140,
              valueGetter: (value, row) => row?.payRun?.reference ?? '',
              renderForExport: (row) => row?.payRun?.reference ?? '',
              renderCell: ({ row }) =>
                row?.payRun ? (
                  <Chip size="small" label={row.payRun.reference} />
                ) : (
                  ''
                ),
            },
            {
              field: 'isPaid',
              headerName: 'Paid',
              valueGetter: (value, row) => (row.isPaid ? 'Yes' : 'No'),
              renderForExport: (row) => (row.isPaid ? 'Yes' : 'No'),
              renderCell: ({ row }) =>
                row.isPaid ? <PriceCheckIcon color="success" /> : '',
              minWidth: 80,
            },
            {
              field: 'hasInvoice',
              headerName: 'Invoice',
              minWidth: 90,
              align: 'center',
              headerAlign: 'center',
              valueGetter: (value, row) =>
                row.workerPaymentType === 'Invoice' && !row.hasInvoice
                  ? 'No Invoice'
                  : '',
              renderForExport: (row) =>
                row.workerPaymentType === 'Invoice' && !row.hasInvoice
                  ? 'No Invoice'
                  : '',
              renderCell: ({ row }) =>
                row.workerPaymentType === 'Invoice' && !row.hasInvoice ? (
                  <Tooltip title="This invoice worker has no invoice submitted for this shift yet">
                    <Chip
                      size="small"
                      icon={<WarningAmberIcon />}
                      label="No Invoice"
                      color="warning"
                      variant="outlined"
                      sx={{ borderRadius: 1 }}
                    />
                  </Tooltip>
                ) : (
                  ''
                ),
            },
          ]}
          rows={results}
          loading={loading}
          documentTitle="Payroll Report"
          onRowClick={({ row }) => setSelectedShift(row)}
          columnVisibilityModel={columnVisibilityModel}
          onColumnVisibilityModelChange={(newModel) =>
            setColumnVisibilityModel(newModel)
          }
          initialState={{
            sorting: {
              sortModel: [{ field: 'employee', sort: 'asc' }],
            },
          }}
          slots={{
            view: null,
            noRowsOverlay: () => (
              <Box
                width="100%"
                height="100%"
                display="flex"
                alignItems="center"
                justifyContent="center"
              >
                <Alert severity="warning">No Shifts Found</Alert>
              </Box>
            ),
          }}
          slotProps={{
            toolbar: {
              printOptions: {
                fields: Object.entries(columnVisibilityModel)

                  .filter(([, visible]) => visible !== false)

                  .map(([field]) => field),
              },
            },
          }}
          checkboxSelection
          disableRowSelectionOnClick
          onRowSelectionModelChange={(newSelectionModel) =>
            setHighlightedShifts(newSelectionModel)
          }
          rowSelectionModel={highlightedShifts}
          isRowSelectable={(params) =>
            !!params.row.assignedTo &&
            params.row.isVerified &&
            !params.row.isPaid &&
            !params.row.payRun
          }
          hideFilters
        />
      </Stack>
      <UnsavedChanges
        changesPresent={
          highlightedShifts?.length + (selectedExpenses?.length ?? 0)
        }
        handleReset={() => {
          setHighlightedShifts([])
          setSelectedExpenses([])
        }}
        handleSave={() =>
          handleCreatePayRun(highlightedShifts, selectedExpenses)
        }
        title={[
          highlightedShifts.length
            ? `${highlightedShifts.length} shift${highlightedShifts.length > 1 ? 's' : ''}`
            : null,
          selectedExpenses?.length
            ? `${selectedExpenses.length} expense${selectedExpenses.length > 1 ? 's' : ''}`
            : null,
        ]
          .filter(Boolean)
          .join(' & ')
          .concat(' selected')}
        saveButtonText="Create Pay Run"
        saveIcon={<PriceCheckIcon />}
      />
    </>
  )
}

export default SearchResults
